import {
  Book,
  BuyBookPayload,
  CreateFavoriteDto,
  Favorite,
  FetchBooksQuery,
  FetchBooksResponse,
  FetchMyBooksQuery,
  Order,
  OrdersState,
  ReadingStats,
  UpdateProgressPayload,
} from "@/app/types";
import apiClient from "@/app/utils/apiClient";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

// =======================================================================
// =========================    FETCHING ADMIN UPLOADED BOOKS      =======
// =======================================================================

// fetching books
export const fetchBooks = createAsyncThunk<
  FetchBooksResponse,
  FetchBooksQuery,
  { rejectValue: string }
>("books/fetchBooks", async (query, { rejectWithValue }) => {
  try {
    const response = await apiClient.get("/books", { params: query });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch books",
    );
  }
});

//=========================================
// ========== FETCHING ADMIN BOOKS ========
//=========================================

// =======================================================================
// ======    BUYING ADMIN UPLOADED BOOKS & USER BOOKS TRACKING     =======
// =======================================================================

// BUY BOOKS
export const buyBook = createAsyncThunk<
  Order,
  BuyBookPayload,
  { rejectValue: string }
>("orders/buyBook", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiClient.post("/orders/buy", payload);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to purchase book",
    );
  }
});

// FETCH USER'S BUYED BOOKS
export const fetchMyBooks = createAsyncThunk<
  // Order[],
  {
    data: Order[];
    pagination: any;
  },
  FetchMyBooksQuery | undefined,
  { rejectValue: string }
>("orders/fetchMyBooks", async (query, { rejectWithValue }) => {
  try {
    const res = await apiClient.get("/orders/my-books", {
      params: query, // 🔥 search, category, status backend ko bhej dega
    });
    return res.data;
  } catch (err: any) {
    if (err.response?.status === 401) return rejectWithValue("SILENT");
    return rejectWithValue(
      err.response?.data?.message || "Failed to load purchased books",
    );
  }
});

// export const fetchMyBooks = createAsyncThunk<
//   { orders: Order[]; pagination: any },
//   FetchMyBooksQuery | undefined,
//   { rejectValue: string }
// >("orders/fetchMyBooks", async (query, { rejectWithValue }) => {
//   try {
//     const res = await apiClient.get("/orders/my-books", {
//       params: query,
//     });
//     return res.data;
//   } catch (err: any) {
//     if (err.response?.status === 401) return rejectWithValue("SILENT");
//     return rejectWithValue(
//       err.response?.data?.message || "Failed to load purchased books",
//     );
//   }
// });

// UPDATE BOOK PROGRESS
export const updateBookProgress = createAsyncThunk<
  Order,
  UpdateProgressPayload,
  { rejectValue: string }
>("orders/updateProgress", async (payload, { rejectWithValue }) => {
  try {
    const updateProgress = {
      progress: payload.progress,
      pagesRead: payload.pagesRead,
    };

    const res = await apiClient.patch(
      `/orders/${payload.orderId}/progress`,
      updateProgress,
    );
    return res.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to update progress",
    );
  }
});

export const fetchReadingStats = createAsyncThunk<
  ReadingStats,
  void,
  { rejectValue: string }
>("orders/fetchReadingStats", async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get("/orders/stats");
    return res.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to load reading statistics",
    );
  }
});

// =======================================================================
// ======    BUYING ADMIN UPLOADED BOOKS & USER BOOKS TRACKING     =======
// =======================================================================

export const fetchSuggestedBooks = createAsyncThunk<
  Book[],
  void,
  { rejectValue: string }
>("orders/fetchSuggestedBooks", async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get("/orders/suggested");
    console.log(res.data, "related books");
    return res.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to load suggested books",
    );
  }
});

const initialState: OrdersState = {
  myBooks: [],
  loading: false,
  error: null,

  books: [],
  suggested: [],

  page: 1,
  limit: 12,
  total: 0,

  stats: null,
  pagination: null,
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    //=========================================
    // ========== FETCHING ADMIN BOOKS ========
    //=========================================

    builder
      .addCase(fetchBooks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchBooks.fulfilled,
        (state, action: PayloadAction<FetchBooksResponse>) => {
          state.loading = false;
          state.books = action.payload.books;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.total = action.payload.total;
        },
      )
      .addCase(fetchBooks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch books";
      });

    // 🚀 Buy Book
    builder
      .addCase(buyBook.pending, (state) => {
        state.loading = true;
      })
      .addCase(buyBook.fulfilled, (state, action) => {
        state.loading = false;
        state.myBooks.push(action.payload); // add purchased book
      })
      .addCase(buyBook.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to buy book";
      });

    // 🚀 Load My Books
    builder
      .addCase(fetchMyBooks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyBooks.fulfilled, (state, action) => {
        state.loading = false;
        // state.myBooks = action.payload;
        state.myBooks = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchMyBooks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load books";
      });

    // 🚀 Update Progress
    builder
      .addCase(updateBookProgress.pending, (state) => {
        // state.loading = true;
      })
      .addCase(updateBookProgress.fulfilled, (state, action) => {
        // state.loading = false;

        // update the specific book
        const index = state.myBooks.findIndex(
          (b) => b._id === action.payload._id,
        );
        if (index !== -1) {
          state.myBooks[index] = action.payload;
        }
      })
      .addCase(updateBookProgress.rejected, (state, action) => {
        // state.loading = false;
        state.error = action.payload || "Failed to update progress";
      });

    // 🚀 Fetch Suggested Books
    builder
      .addCase(fetchSuggestedBooks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSuggestedBooks.fulfilled,
        (state, action: PayloadAction<Book[]>) => {
          state.loading = false;
          state.suggested = action.payload; // 🔥 store here
        },
      )
      .addCase(fetchSuggestedBooks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load suggested books";
      })
      .addCase(fetchReadingStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReadingStats.fulfilled,
        (state, action: PayloadAction<ReadingStats>) => {
          state.loading = false;
          state.stats = action.payload;
        },
      )
      .addCase(fetchReadingStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load statistics";
      });
  },
});

export default ordersSlice.reducer;
